import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCurriculumByClass } from "../../../services/operations/curriculumAPI";
import { submitAssessment } from "../../../services/operations/teacherAssessmentAPI";
import { getAllTeachers } from "../../../services/operations/authAPI";
import { useTranslation } from "react-i18next";

const classes = ["6-A", "6-B", "7-A", "7-B", "8-A", "8-B", "9-A", "9-B", "10-A", "10-B"];
const ratingOptions = [1, 2, 3, 4, 5];

export default function TeacherAssessmentForm() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.profile);
  const { t } = useTranslation();

  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [formData, setFormData] = useState({
    teacherName: "",
    subject: "",
    classSection: "",
    ratings: {
      classroomRespect: {
        treatsAllWithRespect: 3,
        feelSafeAskingQuestions: 3,
        classroomOrderly: 3,
      },
      lessonClarity: {
        clearGoals: 3,
        understandNewTopics: 3,
        examplesHelpUnderstanding: 3,
      },
      engagementChallenge: {
        asksThoughtfulQuestions: 3,
        activitiesHelpLearning: 3,
        encouragesBestWork: 3,
      },
      individualSupport: {
        helpsWhenConfused: 3,
        givesUsefulFeedback: 3,
        providesExtraChallenges: 3,
      },
    },
    openEnded: { whatHelpsYouLearnMost: "", suggestionForImprovement: "" },
  });

  // Fetch teachers
  useEffect(() => {
    const loadTeachers = async () => {
      try {
        const data = await dispatch(getAllTeachers());
        if (data && data.success && data.teachers) {
          setTeachers(data.teachers);
        }
      } catch (error) {
        console.error("Failed to fetch teachers:", error);
      }
    };
    loadTeachers();
  }, [dispatch]);

  // Fetch curriculum data
  useEffect(() => {
    const loadCurriculum = async () => {
      if (!user?.class) return;
      const data = await dispatch(fetchCurriculumByClass());
      if (data && data.subjects) {
        const subjectList = Object.keys(data.subjects).map((key) => ({
          id: key,
          name: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
        }));
        setSubjects(subjectList);
      }
    };
    loadCurriculum();
  }, [user?.class, dispatch]);

  const handleChange = (e, category, field) => {
    const value = e.target.value;
    if (category && field) {
      setFormData((prev) => ({
        ...prev,
        ratings: {
          ...prev.ratings,
          [category]: { ...prev.ratings[category], [field]: Number(value) },
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [e.target.name]: value }));
    }
  };

  const handleOpenEndedChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      openEnded: { ...prev.openEnded, [e.target.name]: e.target.value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const result = await submitAssessment(formData)();
    if (result) {
      setFormData({
        teacherName: "",
        subject: "",
        classSection: "",
        ratings: {
          classroomRespect: {
            treatsAllWithRespect: 3,
            feelSafeAskingQuestions: 3,
            classroomOrderly: 3,
          },
          lessonClarity: {
            clearGoals: 3,
            understandNewTopics: 3,
            examplesHelpUnderstanding: 3,
          },
          engagementChallenge: {
            asksThoughtfulQuestions: 3,
            activitiesHelpLearning: 3,
            encouragesBestWork: 3,
          },
          individualSupport: {
            helpsWhenConfused: 3,
            givesUsefulFeedback: 3,
            providesExtraChallenges: 3,
          },
        },
        openEnded: { whatHelpsYouLearnMost: "", suggestionForImprovement: "" },
      });
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4">
      <h1 className="text-xl font-bold mb-4">{t("teacherAssessment.title")}</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Teacher Name */}
        <div>
          <label>{t("teacherAssessment.teacherName")}</label>
          <select
            name="teacherName"
            value={formData.teacherName}
            onChange={handleChange}
            required
            className="border p-2 w-full"
          >
            <option value="">{t("teacherAssessment.selectTeacher")}</option>
            {teachers.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* Subject */}
        <div>
          <label>{t("teacherAssessment.subject")}</label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="border p-2 w-full"
          >
            <option value="">{t("teacherAssessment.selectSubject")}</option>
            {subjects.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {/* Class Section */}
        <div>
          <label>{t("teacherAssessment.classSection")}</label>
          <select
            name="classSection"
            value={formData.classSection}
            onChange={handleChange}
            required
            className="border p-2 w-full"
          >
            <option value="">{t("teacherAssessment.selectClass")}</option>
            {classes.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        {/* Ratings Sections */}
        {[
          { key: "classroomRespect", label: t("teacherAssessment.classroomRespect") },
          { key: "lessonClarity", label: t("teacherAssessment.lessonClarity") },
          { key: "engagementChallenge", label: t("teacherAssessment.engagementChallenge") },
          { key: "individualSupport", label: t("teacherAssessment.individualSupport") },
        ].map((section) => (
          <div key={section.key}>
            <h2 className="font-semibold mt-4">{section.label}</h2>
            {Object.entries(formData.ratings[section.key]).map(([key, value]) => (
              <div key={key}>
                <label>{t(`teacherAssessment.questions.${key}`)}</label>
                <select
                  value={value}
                  onChange={(e) => handleChange(e, section.key, key)}
                  className="border p-2 w-full"
                >
                  {ratingOptions.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ))}

        {/* Open-Ended */}
        <div>
          <label>{t("teacherAssessment.whatHelps")}</label>
          <textarea
            name="whatHelpsYouLearnMost"
            value={formData.openEnded.whatHelpsYouLearnMost}
            onChange={handleOpenEndedChange}
            className="border p-2 w-full"
            rows={3}
          />
        </div>

        <div>
          <label>{t("teacherAssessment.suggestion")}</label>
          <textarea
            name="suggestionForImprovement"
            value={formData.openEnded.suggestionForImprovement}
            onChange={handleOpenEndedChange}
            className="border p-2 w-full"
            rows={3}
          />
        </div>

        <button type="submit" className="bg-[#00254D] text-white p-2 rounded">
          {t("teacherAssessment.submit")}
        </button>
      </form>
    </div>
  );
}
